import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface FilterPopoverProps {
  isDarkMode: boolean;
  /** Cuántos filtros no están en su valor por defecto: pinta el contador. */
  activeCount: number;
  /** Devuelve todos los filtros a su valor por defecto. */
  onClear?: () => void;
  /** Los mismos <Select> que antes vivían sueltos en el encabezado. */
  children: ReactNode;
}

/**
 * Botón de ícono que guarda los filtros de una sección de flotilla en un
 * popover. Antes cada sección pintaba 2-3 selects de 180-200 px en el
 * encabezado y el buscador quedaba relegado; ahora sólo el buscador ocupa fila.
 */
export function FilterPopover({
  isDarkMode,
  activeCount,
  onClear,
  children,
}: FilterPopoverProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Los menús de react-select van en un portal a <body>, fuera del popover en
  // el DOM pero dentro en el árbol de React: el mousedown sintético sí burbujea
  // hasta acá y llega antes que el listener nativo de document, así que esta
  // bandera distingue "clic en una opción del select" de "clic afuera".
  const insideRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    // El mousedown que abrió el popover dejó la bandera en true sin listener
    // que la limpiara; sin esto el primer clic afuera no cerraba.
    insideRef.current = false;
    const onDocMouseDown = () => {
      if (!insideRef.current) setOpen(false);
      insideRef.current = false;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const label = t("filterPopover.title", "Filtros");
  const active = activeCount > 0;

  return (
    <div
      className="relative shrink-0"
      onMouseDown={() => {
        insideRef.current = true;
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={active ? `${label} (${activeCount})` : label}
        title={label}
        className={`relative h-9 w-9 flex items-center justify-center rounded-lg border transition-[background-color,color,transform] duration-150 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
          open || active
            ? "border-primary-500/60 text-primary-500 bg-primary-500/10"
            : isDarkMode
              ? "border-dark-border bg-dark-card text-gray-400 hover:text-white"
              : "border-gray-200 bg-gray-50 text-gray-600 hover:text-gray-900"
        }`}
      >
        <i className="ri-filter-3-line text-base" aria-hidden="true"></i>
        {active && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-primary-500 text-gray-900 text-[10px] font-semibold leading-4 text-center tabular-nums">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={label}
          className={`absolute right-0 top-full mt-2 z-90 w-72 max-w-[calc(100vw-2rem)] origin-top-right animate-popIn motion-reduce:animate-none rounded-xl border p-3 shadow-xl ${
            isDarkMode
              ? "bg-dark-surface border-dark-border"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span
              className={`text-xs font-semibold uppercase tracking-wide ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {label}
            </span>
            {onClear && active && (
              <button
                type="button"
                onClick={onClear}
                className="text-xs font-medium text-primary-500 hover:underline"
              >
                {t("filterPopover.clear", "Limpiar")}
              </button>
            )}
          </div>
          <div className="flex flex-col gap-2 [&>*]:w-full">{children}</div>
        </div>
      )}
    </div>
  );
}
